/**
 * Parwaaz — ICAO 9303 machine-readable-zone parsing.
 * Full support for TD3 (passport, 2 lines × 44 chars). TD1 (ID card, 3 lines × 30 chars) is
 * detected but not yet parsed — structured so it can be added without touching the TD3 path.
 */
import type { MrzResult } from './types';

const TD3_LINE_LEN = 44;
const TD1_LINE_LEN = 30;

/** Uppercase, strip whitespace, pad/truncate to `len` with the MRZ filler character `<`. */
export function normaliseMrzLine(s: string, len: number = TD3_LINE_LEN): string {
  const cleaned = s.toUpperCase().replace(/\s+/g, '');
  return cleaned.length >= len ? cleaned.slice(0, len) : cleaned.padEnd(len, '<');
}

const CHAR_VALUE: Record<string, number> = { '<': 0 };
for (let i = 0; i < 10; i++) CHAR_VALUE[String(i)] = i;
for (let i = 0; i < 26; i++) CHAR_VALUE[String.fromCharCode(65 + i)] = 10 + i;

/** ICAO 9303 check digit (weights 7-3-1, repeating) for a MRZ field. */
export function mrzCheckDigit(s: string): number {
  const weights = [7, 3, 1];
  let sum = 0;
  for (let i = 0; i < s.length; i++) {
    const v = CHAR_VALUE[s[i]] ?? 0;
    sum += v * weights[i % 3];
  }
  return sum % 10;
}

function checkDigitMatches(field: string, digitChar: string): boolean {
  if (!/^[0-9]$/.test(digitChar)) return false;
  return mrzCheckDigit(field) === Number(digitChar);
}

/** Split a MRZ name field (`SURNAME<<GIVEN<NAMES<<<...`) into surname and given names. */
function splitNameField(field: string): { surname: string; givenNames: string } {
  const idx = field.indexOf('<<');
  const rawSurname = idx === -1 ? field : field.slice(0, idx);
  const rawGiven = idx === -1 ? '' : field.slice(idx + 2);
  const clean = (s: string) => s.replace(/</g, ' ').trim().replace(/\s+/g, ' ');
  return { surname: clean(rawSurname), givenNames: clean(rawGiven) };
}

/** YYMMDD → YYYY-MM-DD. `isExpiry` forces the 2000s; otherwise a century heuristic applies. */
function expandDate(yymmdd: string, isExpiry: boolean, now: Date): string | '' {
  if (!/^[0-9]{6}$/.test(yymmdd)) return '';
  const yy = Number(yymmdd.slice(0, 2));
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  let century: number;
  if (isExpiry) {
    century = 2000;
  } else {
    const currentYy = now.getUTCFullYear() % 100;
    century = yy > currentYy ? 1900 : 2000;
  }
  return `${century + yy}-${mm}-${dd}`;
}

function emptyResult(reason: string, line1 = '', line2 = ''): MrzResult {
  return {
    ok: false,
    reason,
    checks: { docNo: false, dob: false, expiry: false, composite: false },
    documentNumber: '',
    issuingCountry: '',
    nationality: '',
    surname: '',
    givenNames: '',
    sex: '',
    dateOfBirth: '',
    dateOfExpiry: '',
    personalNumber: '',
    line1,
    line2,
  };
}

function parseTd3(rawLines: [string, string], now: Date): MrzResult {
  const line1 = normaliseMrzLine(rawLines[0], TD3_LINE_LEN);
  const line2 = normaliseMrzLine(rawLines[1], TD3_LINE_LEN);

  const issuingCountry = line1.slice(2, 5);
  const { surname, givenNames } = splitNameField(line1.slice(5));

  const documentNumber = line2.slice(0, 9);
  const docNoCheck = line2[9];
  const nationality = line2.slice(10, 13);
  const dobRaw = line2.slice(13, 19);
  const dobCheck = line2[19];
  const sex = line2[20];
  const expiryRaw = line2.slice(21, 27);
  const expiryCheck = line2[27];
  const personalNumberRaw = line2.slice(28, 42);
  const personalNumberCheck = line2[42];
  const compositeCheck = line2[43];

  const compositeInput = documentNumber + docNoCheck + dobRaw + dobCheck + expiryRaw + expiryCheck + personalNumberRaw + personalNumberCheck;

  const checks = {
    docNo: checkDigitMatches(documentNumber, docNoCheck),
    dob: checkDigitMatches(dobRaw, dobCheck),
    expiry: checkDigitMatches(expiryRaw, expiryCheck),
    composite: checkDigitMatches(compositeInput, compositeCheck),
  };

  return {
    ok: checks.docNo && checks.dob && checks.expiry && checks.composite,
    checks,
    documentNumber,
    issuingCountry,
    nationality,
    surname,
    givenNames,
    sex,
    dateOfBirth: expandDate(dobRaw, false, now),
    dateOfExpiry: expandDate(expiryRaw, true, now),
    personalNumber: personalNumberRaw.replace(/</g, ''),
    line1,
    line2,
  };
}

/**
 * Parse a MRZ. Detects TD3 (passport, 2×44) vs TD1 (ID card, 3×30) by line count/length.
 * TD1 is recognised but not yet implemented — returns `ok:false, reason:'TD1 not supported yet'`.
 */
export function parseMrz(lines: string[], now: Date = new Date()): MrzResult {
  if (lines.length === 2) {
    const l1 = normaliseMrzLine(lines[0], TD3_LINE_LEN);
    const l2 = normaliseMrzLine(lines[1], TD3_LINE_LEN);
    if (lines[0].replace(/\s+/g, '').length <= TD3_LINE_LEN && lines[1].replace(/\s+/g, '').length <= TD3_LINE_LEN) {
      return parseTd3([l1, l2], now);
    }
  }
  if (lines.length === 3) {
    return emptyResult('TD1 not supported yet', normaliseMrzLine(lines[0] ?? '', TD1_LINE_LEN), normaliseMrzLine(lines[1] ?? '', TD1_LINE_LEN));
  }
  return emptyResult('unrecognised MRZ format');
}
